import {
  GridColDef,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid-pro';

import { SingleOperators } from 'common/src/query-params/operators';
import {
  IBigQueryBillDto,
  IBigQueryLineItemDto,
} from 'common/src/types/bigQueryBIllDto';
import { IAccount, IUtility } from 'common/src/types/interfaces';
import { IMeterList } from 'common/src/types/meter.dtos';

import { numberFormatter } from 'portal/src/utils/numberFormatter';

import addDateColumnProps from '../common/tables/addDateColumnProps';

export const IN_RANGE_FILTER_OPERATORS: GridFilterOperator[] = [
  {
    label: 'inRange',
    value: SingleOperators.BETWEEN,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const [initialValue, endValue] = filterItem.value;
      if (initialValue && endValue) {
        return ({ value }): boolean =>
          value >= initialValue && value <= endValue;
      }
      return null;
    },
  },
];

export const getBillColumns = (
  accounts: IAccount[],
  utilities: IUtility[],
): GridColDef[] => [
  {
    field: 'startDate',
    filterOperators: IN_RANGE_FILTER_OPERATORS,
    ...addDateColumnProps('Start Date', 90),
  },
  {
    field: 'endDate',
    ...addDateColumnProps('End Date', 90),
  },
  {
    field: 'billAmount',
    headerName: 'Bill Amount',
    valueFormatter: (value) => numberFormatter(value),
    width: 90,
  },
  {
    field: 'calendarBudgetPeriod',
    ...addDateColumnProps('Budget Period'),
    width: 100,
  },
  {
    field: 'budgetPeriodYear',
    headerName: 'Year',
    width: 50,
  },
  {
    field: 'billEstimated',
    headerName: 'Estimated',
    width: 75,
    valueGetter: (value) => {
      if (value !== undefined) return value ? 'Yes' : 'No';
    },
  },
  {
    field: 'utility',
    headerName: 'Utility',
    flex: 1,
    minWidth: 120,
    valueGetter: (_, row: IBigQueryBillDto) => {
      if (!row.accountId) return '';
      const account = accounts.find(({ id }) => id === row.accountId);
      return account?.utility.id;
    },
    valueFormatter: (value) => {
      if (!value) return '';
      const utility = utilities.find(({ id }) => id === value);
      return utility?.name;
    },
  },
  {
    field: 'accountId',
    headerName: 'Account Number',
    flex: 1,
    minWidth: 120,
    valueFormatter: (value) => {
      if (!value) return '';
      return accounts.find(({ id }) => id === value)?.number;
    },
  },
];

export const getLineItemColumns = (meters: IMeterList[]): GridColDef[] => [
  {
    field: 'meterId',
    headerName: 'Meter',
    valueFormatter: (value) =>
      meters.find(({ id }) => id === value)?.name ?? '',
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'facility',
    headerName: 'Facility',
    valueGetter: (_, row: IBigQueryLineItemDto) => {
      const facility = meters.find(
        ({ id }) => id === row.meterId,
      )?.parentFacility;
      return facility?.name;
    },
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'lineItemType',
    headerName: 'Line Item type',
  },
  {
    field: 'lineItemUnit',
    headerName: 'Line Item Unit',
  },
  {
    field: 'lineItemUsage',
    headerName: 'Line Item Usage',
    width: 106,
  },
  {
    field: 'lineItemCost',
    headerName: 'Cost',
    valueFormatter: (value) => numberFormatter(value),
    width: 90,
  },
];
